import { Dispatch, useCallback, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';

import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { IKnmKind } from 'APIPackage/DictionariesModule/interfaces/IKnmKind';
import { getIsPlanErknmStatusUnderConsideration } from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import {
  FIO_SIGNER_BLOCK_ID,
  PLACE_DECISION_BLOCK_ID,
  TITLE_SIGNER_BLOCK_ID,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmErknmKindsSelectOptions,
  getKnmErknmPositionsSelectOptions,
  getKnmFormRef,
  getKnmIsErknmStatusApproving,
  getKnmIsErknmStatusCommentsExist,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusReadyForApprove,
  getKnmIsErknmStatusRejected,
  getKnmIsErknmStatusTypeCompleted,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsNew,
  getKnmSystemType,
  getKnmIsWatchOnly,
  getKnmWatchOnlyByTestUser,
  getKnmIsNeedActSaveOrSign,
  getKnmData,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getPlanListPlanErknmStatus } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IPosition } from 'APIPackage/DictionariesModule/interfaces/IPosition';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { knmTryGenerateDecision } from 'KnmPackage/MainModule/actions/knmActions';
import { UploadFile } from 'UIPackage/MainModule/components/UploadFile';
import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { Hint } from 'CorePackage/MainModule/components/Hint';

import { isLaterThanAdminSetting } from '../../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from '../../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from '../../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { KnmAddressField } from '../../../../KnmPackage/MainModule/components/KnmAddressField';
import { KnmSystemTypesEnum } from '../../../../KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import { Button } from '../../../../UIPackage/MainModule/components/Button';
import styles from './ErknmExcerpt.module.scss';
import { Dropdown } from '../../../../UIPackage/MainModule/components/Dropdown';
import { IDropdownItem } from '../../../../UIPackage/MainModule/interfaces/IDropdownItem';
import { DropdownItem } from '../../../../UIPackage/MainModule/components/DropdownItem';
import { ExportFormatsEnum } from '../../../../APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { getUserRoleIsSupervisor } from '../../../../AuthPackage/MainModule/selectors/authSelectors';
import { ErknmFieldMark } from '../../../MainModule/components/ErknmFieldMark';
import { IKnm } from '../../../../APIPackage/KnmModule/interfaces/IKnm';
import { ExternalSystemSignInfoBlock } from '../../../../CorePackage/MainModule/components/ExternalSystemSignInfoBlock';

interface IErknmExcerptProps {
  created: string | undefined;
  decisionDisabled: boolean | undefined;
  disabledDecisionMessage: string | undefined;
  kindKnmCode: KnmKindCodesEnum | undefined;
  knmId: number | undefined;
  typeCode: IKnmTypeSelectOption['code'] | undefined;
  watchOnlyExtended: boolean;
}

interface IErknmExcerptStoreProps {
  formRef: IKnmFormRef | undefined;
  isWatchOnly: boolean;
  isSupervisor: boolean;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  positionsSelectOptions: IDictionarySelectOption<IPosition>[] | undefined;
  kindsSelectOptions: IDictionarySelectOption<IKnmKind>[] | undefined;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusReadyForApprove: boolean;
  isKnmStatusApproving: boolean;
  isKnmStatusRejected: boolean;
  isKnmStatusCommentsExist: boolean;
  isKnmStatusTypeWaitingCarryOut: boolean;
  isKnmStatusTypeCompleted: boolean;
  isNewKnm: boolean;
  settings: IAdminSetting[] | undefined;
  knmSystemType: KnmSystemTypesEnum | undefined;
  isWatchOnlyByTestUser: boolean;
  isNeedActSaveOrSign?: boolean;
  knmRawData: IKnm | undefined;
}

const ErknmExcerptWithoutStore = ({
  created,
  decisionDisabled,
  disabledDecisionMessage,
  kindKnmCode,
  knmId,
  typeCode,
  watchOnlyExtended,
  formRef,
  isWatchOnly,
  isSupervisor,
  knmPlanStatus,
  positionsSelectOptions,
  kindsSelectOptions,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  isKnmStatusApproving,
  isKnmStatusRejected,
  isKnmStatusCommentsExist,
  isKnmStatusTypeWaitingCarryOut,
  isKnmStatusTypeCompleted,
  isNewKnm,
  settings,
  knmSystemType,
  isWatchOnlyByTestUser,
  isNeedActSaveOrSign = false,
  knmRawData,
}: IErknmExcerptProps & IErknmExcerptStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const { downloadKnmDocumentAttachment } = useKnmDocumentAttachment();

  const isTypeCodeVP = typeCode === KnmTypeCodesEnum.VP;
  const isTypeCodePP248P294 = typeCode === KnmTypeCodesEnum.PP248P294;

  const isValid4121Cert = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_21_CERT_STARTS_FROM,
  );

  const isKnmPlanStatusUnderConsideration: boolean = getIsPlanErknmStatusUnderConsideration(knmPlanStatus);

  const isDisableFieldsInPlanUnderConsideration =
    (isKnmStatusApproving || isKnmStatusCommentsExist || isKnmStatusTypeWaitingCarryOut || isKnmStatusRejected) &&
    isKnmPlanStatusUnderConsideration;

  const isDisableDecisionField =
    isSupervisor &&
    (isDisableFieldsInPlanUnderConsideration ||
      (isTypeCodeVP && !isKnmStatusFillInProcess && !isKnmStatusReadyForApprove && !isNewKnm) ||
      isKnmStatusTypeCompleted ||
      isNeedActSaveOrSign);

  const values = formRef?.current.getState().values;
  const titleSigner = values?.titleSigner;
  const extractPerform = values?.extractPerform;

  const kindOptions =
    kindsSelectOptions && isTypeCodePP248P294
      ? kindsSelectOptions.filter(
          kind =>
            kind.code === KnmKindCodesEnum.DP || kind.code === KnmKindCodesEnum.VP || kind.code === KnmKindCodesEnum.IV,
        )
      : kindsSelectOptions;

  /**
   * Объект Вид КНМ
   */
  const kindKnmObject = useMemo(() => {
    return kindOptions?.find(i => i.code === kindKnmCode);
  }, [kindKnmCode, kindOptions]);

  /**
   * Функция скачивания файла решения
   */
  const handleDownloadDecisionFile = useCallback(
    (isDoc: boolean) => {
      dispatch(knmTryGenerateDecision(knmId?.toString() || '', isDoc));
    },
    [dispatch, knmId],
  );

  const downloadAttachment = () => {
    if (!extractPerform || extractPerform.attachments?.length === 0) return null;

    const attachment = extractPerform.attachments?.[0];

    if (!attachment) return null;

    const { fileName, fileSize } = attachment;
    const filePath = attachment.filePath || null;
    const mimeType = attachment.mimeType || null;

    downloadKnmDocumentAttachment({ fileName, filePath, fileSize, mimeType });
  };

  const dropdownItems: IDropdownItem[] = useMemo(
    () => [
      {
        id: 'export-ods-analytics',
        content: (
          <DropdownItem onClick={() => handleDownloadDecisionFile(false)} format={ExportFormatsEnum.ODS}>
            в формате .odt
          </DropdownItem>
        ),
      },
      {
        id: 'export-doc-analytics',
        content: (
          <DropdownItem onClick={() => handleDownloadDecisionFile(true)} format={ExportFormatsEnum.XLSX}>
            в формате .doc
          </DropdownItem>
        ),
      },
    ],
    [handleDownloadDecisionFile],
  );

  const isShow22 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_22_STARTS_FROM);

  const isEnablePaperlessDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  const isEnablePaperless = settings?.find(
    setting => setting.code === AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_ACT_PRESCRIPTION,
  );

  /**
   * Показывать ли подсказку по необходимости совпадения ФИО сотрудника с ФИО из ЭЦП
   */
  const isShowedSignerTip = isValid4121Cert && (isKnmStatusFillInProcess || isKnmStatusReadyForApprove);

  const isAvailableSingerPosition = !!positionsSelectOptions?.find(participant => participant.id === titleSigner?.id);

  const isShow22AndDecisionDisabled =
    isShow22 &&
    (typeof decisionDisabled === 'boolean' ? decisionDisabled : kindKnmObject && kindKnmObject.decisionDisabled);

  // eslint-disable-next-line no-nested-ternary
  const isTitleSignerDisabled = titleSigner
    ? isAvailableSingerPosition
      ? watchOnlyExtended || isDisableDecisionField
      : false
    : watchOnlyExtended || isDisableDecisionField;

  const isTitleSignerDisabledByTestUser = isWatchOnlyByTestUser || isTitleSignerDisabled;
  const isPaperlessResult = isEnablePaperlessDocument ? isEnablePaperless?.value === 'true' : false;

  return (
    <>
      <FieldBlock
        label={
          <>
            {decisionDisabled ? 'Выписка о проведении КНМ' : 'Решение о проведении КНМ'}
            <ErknmFieldMark sectionName="decision" />
          </>
        }
        className={styles.DropdownWrapper}
        button={
          !isPaperlessResult &&
          isShow22 && (
            <Dropdown items={dropdownItems}>
              <Button
                className="shared-btn-add"
                text="Сформировать"
                variant="link"
                color="primary"
                type="button"
                disabled={isWatchOnly || !isSupervisor || !(isKnmStatusFillInProcess || isKnmStatusReadyForApprove)}
              />
            </Dropdown>
          )
        }
      >
        {isPaperlessResult && !extractPerform && <Hint text="Формируется автоматически после подписания" />}
        {isPaperlessResult && extractPerform && (
          <FieldBlock
            label={'Файл выписки'}
            color="black"
            placementLabel="left"
            type="not-space"
            className={styles.ExtractBlock}
          >
            <UploadFile
              fileName={extractPerform.attachments?.[0]?.fileName || ''}
              fileSize={extractPerform.attachments?.[0]?.fileSize || 0}
              onFileNameClick={downloadAttachment}
            />
          </FieldBlock>
        )}
        {isShow22AndDecisionDisabled && (
          <FieldBlock
            className={styles.DecisionDisabledMessage}
            label={disabledDecisionMessage || kindKnmObject?.disabledDecisionMessage}
            color="black"
          />
        )}
        {!isShow22AndDecisionDisabled && (
          <>
            <FieldBlock
              size="half"
              label={
                <>
                  Дата и время издания решения
                  <ErknmFieldMark fieldName="dateTimeDecision" />
                </>
              }
              placementLabel="left"
              color="black"
            >
              <DatePickerField
                id="dateTimeDecision"
                name="dateTimeDecision"
                timeFormat
                disabled={watchOnlyExtended || isDisableDecisionField}
                fillInProcessMessage
              />
            </FieldBlock>
            <FieldBlock
              margin="top"
              size="half"
              label={
                <>
                  Номер решения
                  <ErknmFieldMark fieldName="numberDecision" />
                </>
              }
              placementLabel="left"
              color="black"
            >
              <TextInputField
                id="numberDecision"
                name="numberDecision"
                className={styles.NumberDecision}
                disabled={watchOnlyExtended || isDisableDecisionField}
              />
            </FieldBlock>
          </>
        )}
      </FieldBlock>
      {!isShow22AndDecisionDisabled && (
        <Row>
          <FieldBlock label="Место вынесения решения" color="black" id={PLACE_DECISION_BLOCK_ID}>
            <KnmAddressField
              id="placeDecision"
              addressFieldPathWithPrefix="placeDecision"
              formRef={formRef}
              resize="none"
              placeholder={
                <>
                  место вынесения решения
                  <ErknmFieldMark fieldName="placeDecision" />
                </>
              }
              disabled={watchOnlyExtended || isDisableDecisionField}
              prefixNameNotInFias={''}
              knmSystemType={knmSystemType}
              manualAddressPlaceholder={
                <>
                  Место
                  <ErknmFieldMark fieldName="manualAddress" />
                </>
              }
              regionPlaceholder={
                <>
                  Субъект РФ
                  <ErknmFieldMark fieldName="region" />
                </>
              }
            />
          </FieldBlock>
        </Row>
      )}
      <Row>
        {knmRawData?.knmErknm?.decision?.systemSignatureUsed ? (
          <ExternalSystemSignInfoBlock
            systemSignatureDateTime={knmRawData?.knmErknm?.decision?.systemSignatureDateTime as string}
            systemSignatureTitle={knmRawData?.knmErknm?.decision?.systemSignatureTitle as string}
          />
        ) : (
          <>
            <FieldBlock
              size="half"
              label={
                <>
                  ФИО должностного лица
                  <ErknmFieldMark fieldName="fioSigner" />
                </>
              }
              color="black"
              id={FIO_SIGNER_BLOCK_ID}
            >
              <TextInputField
                id="fioSigner"
                name="fioSigner"
                disabled={watchOnlyExtended || isDisableDecisionField}
                hadToBeHowInSignMessage={!isShowedSignerTip ? null : 'ФИО должно совпадать со сведениями из ЭЦП'}
              />
            </FieldBlock>
            <FieldBlock
              size="half"
              label={
                <>
                  Должность лица, подписавшего решение
                  <ErknmFieldMark fieldName="titleSigner" />
                </>
              }
              color="black"
              id={TITLE_SIGNER_BLOCK_ID}
            >
              <SelectInputField
                id="titleSigner"
                name="titleSigner"
                options={positionsSelectOptions}
                disabled={isTitleSignerDisabledByTestUser}
                size="medium"
              />
            </FieldBlock>
          </>
        )}
      </Row>
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmExcerptStoreProps => ({
  formRef: getKnmFormRef(state),
  isWatchOnly: getKnmIsWatchOnly(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  positionsSelectOptions: getKnmErknmPositionsSelectOptions(state),
  kindsSelectOptions: getKnmErknmKindsSelectOptions(state),
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusReadyForApprove: getKnmIsErknmStatusReadyForApprove(state),
  isKnmStatusApproving: getKnmIsErknmStatusApproving(state),
  isKnmStatusRejected: getKnmIsErknmStatusRejected(state),
  isKnmStatusCommentsExist: getKnmIsErknmStatusCommentsExist(state),
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isKnmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  isNewKnm: getKnmIsNew(state),
  settings: getAdminFetchSettings(state),
  knmSystemType: getKnmSystemType(state),
  isWatchOnlyByTestUser: getKnmWatchOnlyByTestUser(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
  knmRawData: getKnmData(state),
});

export const ErknmExcerpt = connect(mapStateToProps)(ErknmExcerptWithoutStore);
